import React, { useEffect, useState, useMemo } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import ConfirmModal from '../components/modals/ConfirmModal';
import { useAppStore } from '../store';
import { useAuth } from '../context/AuthContext';

const Archived: React.FC = () => {
  const { userRole } = useAuth();
  const {
    getArchivedProductions,
    getArchivedSamples,
    restoreProduction,
    restoreSample,
    hardDeleteProduction,
    hardDeleteSample,
    clients,
  } = useAppStore();

  const [activeTab, setActiveTab] = useState<'productions' | 'samples'>('productions');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [archivedProductions, setArchivedProductions] = useState<any[]>([]);
  const [archivedSamples, setArchivedSamples] = useState<any[]>([]);
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [sortKey, setSortKey] = useState<'archived_at_desc' | 'archived_at_asc' | 'name_asc' | 'name_desc'>('archived_at_desc');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toRestore, setToRestore] = useState<{ id: string; type: 'production' | 'sample'; name: string } | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<{ id: string; type: 'production' | 'sample'; name: string } | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'productions') {
        const data = await getArchivedProductions();
        setArchivedProductions(data);
      } else {
        const data = await getArchivedSamples();
        setArchivedSamples(data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const list = activeTab === 'productions' ? archivedProductions : archivedSamples;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let result = list;

    // Buscar por nombre
    if (q) {
      result = result.filter((item: any) => `${item.name || ''}`.toLowerCase().includes(q));
    }

    // Filtro por cliente (usando clients del store)
    if (clientFilter !== 'all') {
      result = result.filter((item: any) => item.client_id === clientFilter);
    }

    // Filtro por rango de fechas de archivo
    if (dateFrom) {
      const from = new Date(dateFrom + 'T00:00:00');
      result = result.filter((item: any) => item.archived_at && new Date(item.archived_at) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo + 'T23:59:59');
      result = result.filter((item: any) => item.archived_at && new Date(item.archived_at) <= to);
    }

    // Ordenamiento
    const sorted = [...result].sort((a: any, b: any) => {
      const aName = (a.name || '').toLowerCase();
      const bName = (b.name || '').toLowerCase();
      const aAt = a.archived_at ? new Date(a.archived_at).getTime() : 0;
      const bAt = b.archived_at ? new Date(b.archived_at).getTime() : 0;
      switch (sortKey) {
        case 'archived_at_asc':
          return aAt - bAt;
        case 'name_asc':
          return aName.localeCompare(bName);
        case 'name_desc':
          return bName.localeCompare(aName);
        default:
          return bAt - aAt;
      }
    });

    return sorted;
  }, [list, query, clientFilter, dateFrom, dateTo, sortKey]);

  if (userRole !== 'Administrador') {
    return (
      <div className="p-6">
        <Card className="p-6">
          <p className="text-sm text-gray-600">Acceso restringido.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4 select-none">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Archivados</h1>
      </div>

      <div className="flex gap-2">
        <Button variant={activeTab === 'productions' ? 'primary' : 'secondary'} onClick={() => setActiveTab('productions')}>Producciones</Button>
        <Button variant={activeTab === 'samples' ? 'primary' : 'secondary'} onClick={() => setActiveTab('samples')}>Muestras</Button>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Input value={query} onChange={(e: any) => setQuery(e.target.value)} placeholder="Buscar por nombre" />
          <Select 
            value={clientFilter}
            onChange={(e: any) => setClientFilter(e.target.value)}
            options={[
              { value: 'all', label: 'Todos los clientes' },
              ...clients.map((c: any) => ({ value: c.id, label: c.name }))
            ]}
          />
          <div className="flex gap-2">
            <Input type="date" value={dateFrom} onChange={(e: any) => setDateFrom(e.target.value)} placeholder="Desde" />
            <Input type="date" value={dateTo} onChange={(e: any) => setDateTo(e.target.value)} placeholder="Hasta" />
          </div>
          <Select
            value={sortKey}
            onChange={(e: any) => setSortKey(e.target.value)}
            options={[
              { value: 'archived_at_desc', label: 'Más recientes' },
              { value: 'archived_at_asc', label: 'Más antiguos' },
              { value: 'name_asc', label: 'Nombre A-Z' },
              { value: 'name_desc', label: 'Nombre Z-A' },
            ]}
          />
        </div>
        <div className="mt-3 flex gap-2">
          <Button onClick={loadData} disabled={loading}>{loading ? 'Cargando...' : 'Refrescar'}</Button>
          <Button variant="secondary" onClick={() => { setQuery(''); setClientFilter('all'); setDateFrom(''); setDateTo(''); setSortKey('archived_at_desc'); }}>Limpiar filtros</Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="divide-y">
          {filtered.map((item: any) => (
            <div key={item.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 touch-manipulation">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium truncate">{item.name}</div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                    <span>Archivado: {item.archived_at ? new Date(item.archived_at).toLocaleString() : '-'}</span>
                    <span>Por: {item.archived_by_email || item.archived_by || '-'}</span>
                    <span>Estado original: {item.archived_original_status || '-'}</span>
                    <span>Cliente: {clients.find((c: any) => c.id === item.client_id)?.name || '-'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {activeTab === 'productions' ? (
                    <Button 
                      variant="primary" 
                      onClick={() => { setToRestore({ id: item.id, type: 'production', name: item.name }); setConfirmOpen(true); }}
                    >
                      Restaurar
                    </Button>
                  ) : (
                    <Button 
                      variant="primary" 
                      onClick={() => { setToRestore({ id: item.id, type: 'sample', name: item.name }); setConfirmOpen(true); }}
                    >
                      Restaurar
                    </Button>
                  )}
                  {activeTab === 'productions' ? (
                    <Button 
                      variant="danger" 
                      onClick={() => { setToDelete({ id: item.id, type: 'production', name: item.name }); setConfirmDeleteOpen(true); }}
                    >
                      Eliminar
                    </Button>
                  ) : (
                    <Button 
                      variant="danger" 
                      onClick={() => { setToDelete({ id: item.id, type: 'sample', name: item.name }); setConfirmDeleteOpen(true); }}
                    >
                      Eliminar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {filtered.length === 0 && (
            <div className="p-6 text-sm text-gray-500">No hay elementos archivados.</div>
          )}
        </div>
      </Card>

      <ConfirmModal
        isOpen={confirmOpen}
        title="Confirmar restauración"
        message={toRestore ? `¿Restaurar ${toRestore.type === 'production' ? 'producción' : 'muestra'} "${toRestore.name}"?` : ''}
        confirmText="Restaurar"
        cancelText="Cancelar"
        onConfirm={async () => {
          if (!toRestore) return;
          if (toRestore.type === 'production') {
            await restoreProduction(toRestore.id);
          } else {
            await restoreSample(toRestore.id);
          }
          setConfirmOpen(false);
          setToRestore(null);
          await loadData();
        }}
        onClose={() => { setConfirmOpen(false); setToRestore(null); }}
      />

      <ConfirmModal
        isOpen={confirmDeleteOpen}
        title="Eliminar definitivamente"
        message={toDelete ? `Esta acción no se puede deshacer. ¿Eliminar definitivamente la ${toDelete.type === 'production' ? 'producción' : 'muestra'} "${toDelete.name}"?` : ''}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
        onConfirm={async () => {
          if (!toDelete) return;
          if (toDelete.type === 'production') {
            await hardDeleteProduction(toDelete.id);
          } else {
            await hardDeleteSample(toDelete.id);
          }
          setConfirmDeleteOpen(false);
          setToDelete(null);
          await loadData();
        }}
        onClose={() => { setConfirmDeleteOpen(false); setToDelete(null); }}
      />
    </div>
  );
};

export default Archived;
